import type { Collection, Map as ImmutableMap } from 'immutable';
import type { Interval } from 'luxon';
import { createCachedSelector, LruObjectCache } from 're-reselect';

import { type ACCOUNTS_REDUCER_KEY, accountsSelectors } from 'app-components/flux/accounts';
import { selectorsHelpers } from 'app-components/flux/core';
import { type DATE_REDUCER_KEY, dateSelectors } from 'app-components/flux/date';
import type { FEATURE_FLAGS_REDUCER_KEY } from 'app-components/flux/feature-flags';
import { transactionsSelectors } from 'app-components/flux/transactions';
import type { TRANSACTIONS_REDUCER_KEY, TransactionRecord } from 'app-components/flux/transactions/transactionsTypes';
import { immutableEntriesShallowEqual } from 'app-components/utils/core/reselect';
import { registerCachedSelectors } from 'app-components/utils/core/reselect/selectorCacheRegistry';
import { intervalContainsInclusiveISO } from 'app-components/utils/date/intervalContainsInclusive';
import type RootState from 'app-components/utils/redux-store/rootState';

type State = Pick<
  RootState,
  | typeof ACCOUNTS_REDUCER_KEY
  | typeof DATE_REDUCER_KEY
  | typeof FEATURE_FLAGS_REDUCER_KEY
  | typeof TRANSACTIONS_REDUCER_KEY
>;

type ReportDateProps = { dateRange?: Interval | null };
type Transactions = ImmutableMap<string, TransactionRecord>;
type TransactionsByAccount = Collection.Keyed<
  TransactionRecord['accountId'],
  Collection.Keyed<string, TransactionRecord>
>;

const getStart = (_state: State, props: ReportDateProps): string | undefined =>
  props.dateRange?.start?.toISODate() ?? undefined;
const getEnd = (_state: State, props: ReportDateProps): string | undefined =>
  props.dateRange?.end?.toISODate() ?? undefined;
const getWindowKey = (state: State, props: ReportDateProps): string =>
  `${getStart(state, props) ?? ''}/${getEnd(state, props) ?? ''}`;

const filterReportWindow = selectorsHelpers.createIncrementalFilter(
  (transactions: Transactions, test: (transaction: TransactionRecord) => unknown) => transactions.filter(test),
);

const getTransactionsInReportWindow = createCachedSelector(
  transactionsSelectors.getResourcesById,
  getStart,
  getEnd,
  (transactions, start, end): Transactions =>
    // Edits re-test only the transactions they changed.
    filterReportWindow(
      transactions,
      [start, end],
      (transaction) => intervalContainsInclusiveISO(start, end, transaction.postedOn),
      `${start ?? ''}/${end ?? ''}`,
    ),
  {
    memoizeOptions: {
      resultEqualityCheck: immutableEntriesShallowEqual<string, TransactionRecord>,
    },
  },
)({
  // Dashboard and report screens can keep different date windows mounted together.
  keySelector: getWindowKey,
  cacheObject: new LruObjectCache({ cacheSize: 12 }),
});

const getDatedTransactionsByAccount = createCachedSelector(
  getTransactionsInReportWindow,
  accountsSelectors.getGoalAccountIds,
  dateSelectors.getCurrentDateString,
  (transactions, goalAccountIds, today): TransactionsByAccount =>
    transactions
      .groupBy((transaction) => transaction.accountId)
      .map((accountTransactions) =>
        accountTransactions.filter((transaction) =>
          transactionsSelectors.isTransactionOfTypes(
            transaction,
            goalAccountIds,
            transactionsSelectors.txnTypesDefault,
            today,
          ),
        ),
      ),
)({
  keySelector: getWindowKey,
  cacheObject: new LruObjectCache({ cacheSize: 12 }),
});

/**
 * Keep full report records fresh while excluding other dates before grouping and classification.
 *
 * Not a drop-in for transactionsSelectors.getTransactionsByAccountId: the dated result is keyed by every
 * accountId in the window (including accounts that are not loaded) and omits accounts with no dated rows.
 * getReportFilteredTxns only reads the reportable accounts it iterates, so both shapes yield the same report.
 */
export function getTransactionsByAccountForReport(state: State, props: ReportDateProps): TransactionsByAccount {
  if (!getStart(state, props) && !getEnd(state, props)) {
    // Reports without a date boundary retain the shared all-history path.
    return transactionsSelectors.getTransactionsByAccountId(state, {
      txnTypes: transactionsSelectors.txnTypesDefault,
    });
  }
  return getDatedTransactionsByAccount(state, props);
}

// A store reset drops every key's selector (see selectorCacheRegistry).
registerCachedSelectors(getTransactionsInReportWindow, getDatedTransactionsByAccount);
